import React, { useState, useEffect, useCallback } from 'react'
import classNames from 'classnames'
import useRenderEvent from '@/core/hooks/useRenderEvent'
import { useFeedViewListener } from '@/core/hooks/useFeedViewListener'
import threadsService from '@/app-domains/threads/threads.service'
import sessionService from '@/app-domains/user/session/session.service'
import feedService from '@/app-domains/feed/feed.service'
import debounce from 'lodash.debounce'
import MemoizedThread from '@/pages/HomePage/FeedContent/Thread/Thread'
import NoMessagesComponent from '@/pages/HomePage/FeedContent/NoMessagesComponent'
import SearchingComponent from '@/pages/HomePage/FeedContent/SearchingComponent'
import ExpireTrialModal from '../ExpireTrialModal/ExpireTrialModal'

import { FeedHeader } from '../FeedHeader/FeedHeader'
import commonsService from '@/services/commons.service'

import './FeedContent.scoped.sass'
import modalService from '@/services/modal.service'

export function FeedContent () {
  const debouncedObservation = useCallback(debounce(() => {
    feedService.observeIfMessagesAreRead()
    feedService.setUpShowLessObserver()
  }, 1000), [])
  const [renders, setRenders] = useState(0)

  const { viewType } = useFeedViewListener()

  function getExpireTrialModal () {
    if (sessionService.getMe().me.onboarding && sessionService.getMe().me.onboarding.trialRemainingDays === 3) {
      return setTimeout(() => { modalService.open(<ExpireTrialModal />, true) }, 1000)
    } else {
      return <></>
    }
  }

  // Prevent double observation, only one observer is allowed
  useEffect(() => {
    debouncedObservation()
    getExpireTrialModal()
  }, [])

  const scrollListenerHandler = useCallback((thresholdMultiplier) => {
    feedService.handleFeedScroll(thresholdMultiplier)
  }, [])

  const scrollListener = useCallback((event, thresholdMultiplier) => {
    const feedContent = document.querySelector('.feed-content')
    feedContent.removeEventListener(event, scrollListenerHandler)
    feedContent.addEventListener(event, scrollListenerHandler.bind(this, thresholdMultiplier))
  }, [])

  const addScrollListener = useCallback((viewType = 'normal') => {
    if (!commonsService.isMobile()) scrollListener('scroll', viewType === 'dense' ? 0.33 : 0.66)
    else {
      const threshold = viewType === 'normal' ? 0.35 : 0.2
      scrollListener('scroll', threshold)
      scrollListener('touchmove', threshold)
    }
  }, [viewType])
  
  useEffect(() => addScrollListener('normal'), [])
  useRenderEvent(['UI:view_feed_change'], (type) => addScrollListener(type))

  useRenderEvent(['MESSAGE:EXPANDED:SWITCH'], (shouldObserve) => feedService.updateShowLessObserver(shouldObserve))
  useRenderEvent([
    'THREADS:UPDATED',
    'FILTERS:UPDATED',
    'FILTERS:cleaned'
  ], () => {
    debouncedObservation()
    setRenders(renders + 1)
  })

  useRenderEvent([
    'MESSAGES:deleted',
  ], async () => {
    const currentMessages = threadsService.retrieveFilteredData()

    if (!currentMessages.length) {
      setRenders(renders + 1)
    }
  })

  const sentryBeforeCapture = useCallback((scope, thread) => {
    scope.setUser({ id: thread.userId })
    scope.setTag('thread-id', thread.id)
    scope.setTag('user-id', thread.userId)
  }, [])

  const getThreadComponentsForFeed = (threads) => {
    if (!threads) { return }
    const areFiltersBeingProcessed = threadsService.isQueryingForThreads
    const threadList = Object.values(threads).sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)).map((thread, index) => {
      if (viewType === 'dense' && index > 99) { // FIXME: Do not render more than 100 elements in dense feed
        return undefined
      }
      return <div className="post-card-container" key={thread.id}>
        <MemoizedThread thread={thread} index={index} sentryBeforeCapture={sentryBeforeCapture}/>
      </div>
    })
    if (threadList.length === 0 && !areFiltersBeingProcessed) {
      threadList.push(<div className="post-card-container"><NoMessagesComponent key="no-messages"/></div>)
    } else if (areFiltersBeingProcessed) {
      threadList.push(<div className="post-card-container"><SearchingComponent key="searching"/></div>)
    }
    return threadList
  }

  return (
    <>
      <section id='main-feed-scroll' className={classNames('feed-content scrollable-list', { [viewType]: viewType })}>
        <FeedHeader/>
        {getThreadComponentsForFeed(threadsService.retrieveFilteredData())}
      </section>
    </>
  )
}
